import { convertToRaw } from 'draft-js'

import { INQUIRY_ACTIONS, INQUIRY_STATUS_IDS } from '../../../utils/constants/global-constants'
/**
 * Appends a 0 to any single digits (i.e. 1 === '01', 9 === '09')
 *
 * @param {number} num the number that needs to be converted to doubled digits
 * @returns {string} a minimum of two digits (i.e. '01' || '54' || '231')
 */
const twoDigits = (num) => String(num ?? '00').padStart(2, '0')

/**
 * Escapes single quotes in a given string by replacing them with escaped single quotes.
 * @param {string} inputString - The string in which single quotes need to be escaped.
 * @param {string} replace - What needs to be replaced.
 * @returns {string} - The modified string with single quotes escaped.
 * @throws {TypeError} - Throws an error if the input is not a string.
 */
const formatStringForServer = (inputString: string, replace: string): string => {
  if (typeof inputString !== 'string') {
    throw new TypeError('Input must be a string')
  }
  // Use the replace method to find and escape single quotes
  switch (replace) {
    case 'single-quotes':
      return inputString.replace(/'/g, "'''")
    // Add more cases here if needed
    default:
      // Optional: handle default case if needed
      return inputString
  }
}
const formatStatusCodeToRole = (statusCode) => {
  return statusCode.split('-')[1] // Split the status code by '-' and return the second part (the role)
  .toLowerCase() // Convert the entire string to lowercase
  .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
  .replace(/\s+/g, ' ') // Remove extra spaces if any
  .trim() // Remove leading or trailing spaces
  .replace(/\b(mp[fl]|css)\b/gi, match => match.toUpperCase()) // Always capitalize "MPF" and "CSS"
  .replace(/\b(\w)(\S*)/g, (_, first, rest) => first.toUpperCase() + rest) // Capitalize the first letter of each word and keep the rest lowercase
}
const svgTextWrap = (text, lineLength) => {
  const textArray: string[] = []
  let curWord = ''

  const wordArray = text?.split(' ')
  wordArray.forEach((word, index) => {
    curWord += word
    if (curWord.length >= lineLength || index === wordArray.length - 1) {
      textArray.push(curWord)
      curWord = ''
    } else {
      curWord += ' '
    }
  })

  return textArray
}

/** convertRTE
 * Converts the content of a Rich Text Editor (RTE) state to a plain text string.
 *
 * This function extracts the raw content from the editor state, maps over the blocks
 * to extract the text, and then joins the text blocks into a single string separated by spaces.
 *
 * @param {Object} editorState - The state of the Rich Text Editor.
 * @returns {string} The plain text representation of the RTE content.
 */
const convertRTE = (editorState): string => {
  const editor = editorState.getCurrentContent()
  const convertedRTE = convertToRaw(editor)?.blocks?.map(({ text }) => text)
  const value = convertedRTE?.join(' ') || ''
  return value
}
/**
 * Formats a string by capitalizing the first letter of each word and converting the rest of the letters to lowercase.
 * Words are determined by splitting the string on underscores or spaces.
 * This is useful for transforming strings like 'SECTION_CHIEF' into 'Section Chief'.
 *
 * @param role - The string representing the role to format.
 * @returns {string} - A formatted string with each word capitalized.
 * If the input is null or undefined, an empty string is returned.
 */
const formatRoleName = (role: string): string => {
  if (!role) return '' // Handle null or undefined input
  const formattedRoleName = role
    .split(/[_\s]/) // Split on underscores or spaces
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
    .join(' ') // Join the words with a space
  return formattedRoleName
}

/**
 * Extracts and formats the last segment of a status string separated by hyphens.
 * This function is useful for retrieving and cleaning the final status label
 * from a hyphenated string (e.g., 'Customer-Service-Technician' becomes 'Technician')
 *
 * In the future we should consider simply removing the Section names from the Status since
 * section is already a column.
 *
 * @param status - The hyphen-separated status string.
 * @returns {string} - The last segment of the input string, trimmed of any whitespace.
 * If the input is null or undefined, an empty string is returned.
 */
const formatStatusName = (status: string | undefined): string => {
  if (!status) {
    return ''
  }
  const words = status.split('-')
  return words[words.length - 1].trim()
}

const formatFileDuplicates = (files: string[]): string => {
  if (files.length === 0) return ''
  if (files.length === 1) return files[0]

  const lastFile = files.pop()
  return `${files.join(', ')} and ${lastFile}`
}
const convertToPlainText = (str) => {
  return str.replace(/[_-]/g, ' ')
}

/**
 * Determines the appropriate status code based on the current servicing entity and the provided status code.
 *
 * @param statusCode - The original status code in the format "SECTION-ROLE".
 * @param currentlyServicingBy - Specifies the entity currently servicing, either 'CSS' or 'MPF'.
 *
 * @returns A new status code string:
 *          - If the role in the original status code is 'SITEADMIN':
 *            - Returns "SECTION-TECHNICIAN" if `currentlyServicingBy` is 'MPF'.
 *            - Returns "SECTION-CSS" if `currentlyServicingBy` is 'CSS'.
 *          - Otherwise, returns the original `statusCode` unchanged.
 */

const determineStatusCode = (statusCode: string, currentlyServicingBy: 'CSS' | 'MPF'): string => {
  const [section, fromStatusCode] = statusCode.split('-')

  if (fromStatusCode === 'SITEADMIN') {
    return `${section}-${currentlyServicingBy === 'MPF' ? 'TECHNICIAN' : 'CSS'}`
  }

  if (fromStatusCode === 'MPF_LEADERSHIP' || statusCode === 'MPF_LEADERSHIP') {
    return fromStatusCode ?? statusCode
  }

  return statusCode
}

const convertRoleToAction = (str: string): string => {
  return [
    INQUIRY_STATUS_IDS.CareerDevelopmentCSS,
    INQUIRY_STATUS_IDS.CareerDevelopmentSectionChief,
    INQUIRY_STATUS_IDS.CareerDevelopmentSupervisor,
    INQUIRY_STATUS_IDS.CareerDevelopmentTechnician,
    INQUIRY_STATUS_IDS.CustomerSupportCSS,
    INQUIRY_STATUS_IDS.CustomerSupportSectionChief,
    INQUIRY_STATUS_IDS.CustomerSupportSupervisor,
    INQUIRY_STATUS_IDS.CustomerSupportTechnician,
    INQUIRY_STATUS_IDS.ForceManagementCSS,
    INQUIRY_STATUS_IDS.ForceManagementSectionChief,
    INQUIRY_STATUS_IDS.ForceManagementSupervisor,
    INQUIRY_STATUS_IDS.ForceManagementTechnician,
    INQUIRY_STATUS_IDS.InstallationPersonnelReadinessCSS,
    INQUIRY_STATUS_IDS.InstallationPersonnelReadinessSectionChief,
    INQUIRY_STATUS_IDS.InstallationPersonnelReadinessSupervisor,
    INQUIRY_STATUS_IDS.InstallationPersonnelReadinessTechnician,
    INQUIRY_STATUS_IDS.MpfLeadership,
    INQUIRY_STATUS_IDS.PersonnelSystemsManagementCSS,
    INQUIRY_STATUS_IDS.PersonnelSystemsManagementSectionChief,
    INQUIRY_STATUS_IDS.PersonnelSystemsManagementSupervisor,
    INQUIRY_STATUS_IDS.PersonnelSystemsManagementTechnician,
  ].includes(str)
    ? INQUIRY_ACTIONS.RESPONDED
    : INQUIRY_ACTIONS[str]
}

//defaultToEmptyString: Purpose is to ensure there are no undefined values being displayed to customer.
const defaultToEmptyString = (val) => {
  if (val === undefined || val === 'undefined') {
    return ''
  } else {
    return val
  }
}
const getRoleFromStatusDesc = (statusDesc: string): string => {
  const hyphenIndex = statusDesc?.indexOf('-')

  if (hyphenIndex !== -1) {
    return statusDesc?.slice(hyphenIndex + 1)
  }
  return statusDesc
}

export {
  convertRTE,
  convertRoleToAction,
  convertToPlainText,
  defaultToEmptyString,
  determineStatusCode,
  formatFileDuplicates,
  formatRoleName,
  formatStatusCodeToRole,
  formatStatusName,
  formatStringForServer,
  getRoleFromStatusDesc,
  svgTextWrap,
  twoDigits,
}
